import { supabase } from './supabase'
import { listDoctorAccessesForPatient } from './doctor-access'
import type { ActiveMedication, Medication, MedicationHistory, MedicationInput } from '@/types/health'

const DOCTOR_PREFIX_PATTERN = /^(dr\.?\s*|docteur\s+|docteure\s+)/i

function isMissingRelationError(error: unknown, relationNames: string[]): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }

  const code = String((error as { code?: unknown }).code || '')
  const message = String((error as { message?: unknown }).message || '')

  if (code !== 'PGRST205' && code !== '42P01') {
    return false
  }

  return relationNames.some((relationName) => message.includes(relationName))
}

function hasOwnKey<T extends object>(value: T, key: keyof T) {
  return Object.prototype.hasOwnProperty.call(value, key)
}

function normalizeOptionalText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null
  }

  const normalized = value.trim().replace(/\s+/g, ' ')
  return normalized || null
}

function normalizeOptionalDate(value: unknown): string | null {
  return normalizeOptionalText(value)
}

function normalizeMetadata(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

export function normalizeMedicationPrescriberName(value: unknown): string | null {
  const normalized = normalizeOptionalText(value)

  if (!normalized) {
    return null
  }

  return normalized.replace(DOCTOR_PREFIX_PATTERN, '').trim() || null
}

function normalizeSideEffects(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return []
  }

  return [...new Set(
    value
      .map((item) => normalizeOptionalText(item))
      .filter((item): item is string => Boolean(item))
  )]
}

function normalizeMedicationPayload(input: MedicationInput): MedicationInput {
  return {
    patient_id: input.patient_id,
    name: normalizeOptionalText(input.name) || '',
    dosage: normalizeOptionalText(input.dosage),
    frequency: normalizeOptionalText(input.frequency),
    instructions: normalizeOptionalText(input.instructions),
    prescriber_name: normalizeMedicationPrescriberName(input.prescriber_name),
    prescription_date: normalizeOptionalDate(input.prescription_date),
    start_date: normalizeOptionalDate(input.start_date) || '',
    planned_end_date: normalizeOptionalDate(input.planned_end_date),
    actual_end_date: normalizeOptionalDate(input.actual_end_date),
    status: input.status || 'active',
    reason_for_change: normalizeOptionalText(input.reason_for_change),
    side_effects: normalizeSideEffects(input.side_effects),
    effectiveness_rating:
      typeof input.effectiveness_rating === 'number' && Number.isFinite(input.effectiveness_rating)
        ? input.effectiveness_rating
        : null,
    notes: normalizeOptionalText(input.notes),
    metadata: normalizeMetadata(input.metadata)
  }
}

function normalizeMedicationUpdatePayload(
  updates: Partial<MedicationInput>
): Partial<MedicationInput> {
  const payload: Partial<MedicationInput> = {}

  if (hasOwnKey(updates, 'name')) {
    payload.name = normalizeOptionalText(updates.name) || ''
  }

  if (hasOwnKey(updates, 'dosage')) {
    payload.dosage = normalizeOptionalText(updates.dosage)
  }

  if (hasOwnKey(updates, 'frequency')) {
    payload.frequency = normalizeOptionalText(updates.frequency)
  }

  if (hasOwnKey(updates, 'instructions')) {
    payload.instructions = normalizeOptionalText(updates.instructions)
  }

  if (hasOwnKey(updates, 'prescriber_name')) {
    payload.prescriber_name = normalizeMedicationPrescriberName(updates.prescriber_name)
  }

  if (hasOwnKey(updates, 'prescription_date')) {
    payload.prescription_date = normalizeOptionalDate(updates.prescription_date)
  }

  if (hasOwnKey(updates, 'start_date')) {
    payload.start_date = normalizeOptionalDate(updates.start_date) || ''
  }

  if (hasOwnKey(updates, 'planned_end_date')) {
    payload.planned_end_date = normalizeOptionalDate(updates.planned_end_date)
  }

  if (hasOwnKey(updates, 'actual_end_date')) {
    payload.actual_end_date = normalizeOptionalDate(updates.actual_end_date)
  }

  if (hasOwnKey(updates, 'status')) {
    payload.status = updates.status
  }

  if (hasOwnKey(updates, 'reason_for_change')) {
    payload.reason_for_change = normalizeOptionalText(updates.reason_for_change)
  }

  if (hasOwnKey(updates, 'side_effects')) {
    payload.side_effects = normalizeSideEffects(updates.side_effects)
  }

  if (hasOwnKey(updates, 'effectiveness_rating')) {
    payload.effectiveness_rating =
      typeof updates.effectiveness_rating === 'number' && Number.isFinite(updates.effectiveness_rating)
        ? updates.effectiveness_rating
        : null
  }

  if (hasOwnKey(updates, 'notes')) {
    payload.notes = normalizeOptionalText(updates.notes)
  }

  if (hasOwnKey(updates, 'metadata')) {
    payload.metadata = normalizeMetadata(updates.metadata)
  }

  return payload
}

function normalizeDoctorSuggestionKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function getActionFromStatusChange(
  previousStatus: string,
  newStatus: string
): MedicationHistory['action'] {
  if (previousStatus === 'active' && newStatus === 'paused') return 'paused'
  if (previousStatus === 'paused' && newStatus === 'active') return 'resumed'
  if (newStatus === 'stopped') return 'stopped'
  if (newStatus === 'completed') return 'completed'
  return 'modified'
}

function getComputedCurrentStatus(medication: Medication): ActiveMedication['current_status'] {
  if (medication.actual_end_date) {
    return 'stopped'
  }

  if (medication.planned_end_date && medication.planned_end_date < new Date().toISOString().split('T')[0]) {
    return 'expired'
  }

  return medication.status
}

function toActiveMedication(medication: Medication): ActiveMedication {
  return {
    ...medication,
    current_status: getComputedCurrentStatus(medication)
  }
}

export async function createMedication(medication: MedicationInput): Promise<Medication> {
  const payload = normalizeMedicationPayload(medication)
  const validation = validateMedicationInput(payload)

  if (!validation.isValid) {
    throw new Error(validation.errors.join(' '))
  }

  const { data, error } = await supabase
    .from('medications')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la creation du medicament: ${error.message}`)
  }

  try {
    await addMedicationHistory({
      medication_id: data.id,
      patient_id: payload.patient_id,
      action: 'started',
      new_status: payload.status || 'active',
      reason: 'Nouveau medicament ajoute',
      notes: `Medicament ${payload.name} debute`
    })
  } catch (historyError) {
    console.warn('Historique du medicament non enregistre:', historyError)
  }

  return data as Medication
}

export async function getMedications(patientId: string): Promise<Medication[]> {
  const { data, error } = await supabase
    .from('medications')
    .select('*')
    .eq('patient_id', patientId)
    .order('start_date', { ascending: false })

  if (error) {
    throw new Error(`Erreur lors de la recuperation des medicaments: ${error.message}`)
  }

  return (data || []) as Medication[]
}

export async function getActiveMedications(patientId: string): Promise<ActiveMedication[]> {
  const { data, error } = await supabase
    .from('active_medications')
    .select('*')
    .eq('patient_id', patientId)
    .order('start_date', { ascending: false })

  if (error) {
    if (isMissingRelationError(error, ['public.active_medications', 'active_medications'])) {
      const medications = await getMedications(patientId)

      return medications
        .map(toActiveMedication)
        .filter((medication) => medication.current_status !== 'stopped')
        .sort((left, right) => right.start_date.localeCompare(left.start_date))
    }

    throw new Error(`Erreur lors de la recuperation des medicaments actifs: ${error.message}`)
  }

  return (data || []) as ActiveMedication[]
}

export async function getMedicationPrescriberSuggestions(patientId: string): Promise<string[]> {
  const suggestions = new Map<string, string>()

  const collect = (value: unknown) => {
    const normalizedValue = normalizeMedicationPrescriberName(value)

    if (!normalizedValue) {
      return
    }

    const key = normalizeDoctorSuggestionKey(normalizedValue)

    if (!key || suggestions.has(key)) {
      return
    }

    suggestions.set(key, normalizedValue)
  }

  const [medicationsResult, documentsResult, doctorAccessesResult] = await Promise.allSettled([
    supabase
      .from('medications')
      .select('prescriber_name')
      .eq('patient_id', patientId)
      .not('prescriber_name', 'is', null),
    supabase
      .from('medical_documents')
      .select('practitioner_name')
      .eq('patient_id', patientId)
      .not('practitioner_name', 'is', null),
    listDoctorAccessesForPatient(patientId)
  ])

  if (medicationsResult.status === 'fulfilled' && !medicationsResult.value.error) {
    for (const row of medicationsResult.value.data || []) {
      collect((row as { prescriber_name?: string | null }).prescriber_name)
    }
  }

  if (documentsResult.status === 'fulfilled' && !documentsResult.value.error) {
    for (const row of documentsResult.value.data || []) {
      collect((row as { practitioner_name?: string | null }).practitioner_name)
    }
  }

  if (doctorAccessesResult.status === 'fulfilled') {
    for (const grant of doctorAccessesResult.value) {
      collect(grant.doctor_name)
    }
  }

  return [...suggestions.values()].sort((left, right) => left.localeCompare(right, 'fr'))
}

export async function updateMedication(
  medicationId: string,
  updates: Partial<MedicationInput>,
  reason?: string
): Promise<Medication> {
  const { data: currentMedicationData, error: fetchError } = await supabase
    .from('medications')
    .select('*')
    .eq('id', medicationId)
    .single()

  if (fetchError) {
    throw new Error(`Erreur lors de la recuperation du medicament: ${fetchError.message}`)
  }

  const currentMedication = currentMedicationData as Medication
  const sanitizedUpdates = normalizeMedicationUpdatePayload(updates)

  if (Object.keys(sanitizedUpdates).length === 0) {
    return currentMedication
  }

  const validation = validateMedicationInput(
    normalizeMedicationPayload({
      ...currentMedication,
      ...sanitizedUpdates
    })
  )

  if (!validation.isValid) {
    throw new Error(validation.errors.join(' '))
  }

  const { data, error } = await supabase
    .from('medications')
    .update(sanitizedUpdates)
    .eq('id', medicationId)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la mise a jour du medicament: ${error.message}`)
  }

  if (sanitizedUpdates.status && sanitizedUpdates.status !== currentMedication.status) {
    try {
      await addMedicationHistory({
        medication_id: medicationId,
        patient_id: currentMedication.patient_id,
        action: getActionFromStatusChange(currentMedication.status, sanitizedUpdates.status),
        previous_status: currentMedication.status,
        new_status: sanitizedUpdates.status,
        previous_dosage: currentMedication.dosage,
        new_dosage: sanitizedUpdates.dosage || currentMedication.dosage,
        reason: reason || 'Modification du medicament'
      })
    } catch (historyError) {
      console.warn('Historique du medicament non enregistre:', historyError)
    }
  }

  return data as Medication
}

export async function stopMedication(
  medicationId: string,
  reason: string,
  stopDate?: string
): Promise<Medication> {
  const actualEndDate = stopDate || new Date().toISOString().split('T')[0]

  return updateMedication(
    medicationId,
    {
      status: 'stopped',
      actual_end_date: actualEndDate,
      reason_for_change: reason
    },
    `Medicament arrete: ${reason}`
  )
}

export async function pauseMedication(medicationId: string, reason: string): Promise<Medication> {
  return updateMedication(
    medicationId,
    {
      status: 'paused',
      reason_for_change: reason
    },
    `Medicament mis en pause: ${reason}`
  )
}

export async function resumeMedication(medicationId: string, reason?: string): Promise<Medication> {
  return updateMedication(
    medicationId,
    {
      status: 'active',
      reason_for_change: reason || 'Reprise du traitement'
    },
    `Medicament repris: ${reason || 'Reprise du traitement'}`
  )
}

export async function addMedicationHistory(history: {
  medication_id: string
  patient_id: string
  action: MedicationHistory['action']
  previous_status?: string | null
  new_status?: string | null
  previous_dosage?: string | null
  new_dosage?: string | null
  reason?: string | null
  notes?: string | null
}): Promise<MedicationHistory> {
  const { data, error } = await supabase
    .from('medication_history')
    .insert({
      ...history,
      changed_by: (await supabase.auth.getUser()).data.user?.id || null
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de l'ajout a l'historique: ${error.message}`)
  }

  return data as MedicationHistory
}

export async function getMedicationHistory(medicationId: string): Promise<MedicationHistory[]> {
  const { data, error } = await supabase
    .from('medication_history')
    .select('*')
    .eq('medication_id', medicationId)
    .order('change_date', { ascending: false })

  if (error) {
    if (isMissingRelationError(error, ['public.medication_history', 'medication_history'])) {
      return []
    }

    throw new Error(`Erreur lors de la recuperation de l'historique: ${error.message}`)
  }

  return (data || []) as MedicationHistory[]
}

export async function getPatientMedicationHistory(patientId: string): Promise<MedicationHistory[]> {
  const { data, error } = await supabase
    .from('medication_history')
    .select(`
      *,
      medication:medications(name)
    `)
    .eq('patient_id', patientId)
    .order('change_date', { ascending: false })

  if (error) {
    if (isMissingRelationError(error, ['public.medication_history', 'medication_history'])) {
      return []
    }

    throw new Error(`Erreur lors de la recuperation de l'historique patient: ${error.message}`)
  }

  return (data || []) as MedicationHistory[]
}

export function getMedicationStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return 'text-green-400 bg-green-400/10 border-green-400/20'
    case 'paused':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20'
    case 'stopped':
      return 'text-red-400 bg-red-400/10 border-red-400/20'
    case 'completed':
      return 'text-blue-400 bg-blue-400/10 border-blue-400/20'
    case 'expired':
      return 'text-orange-400 bg-orange-400/10 border-orange-400/20'
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/20'
  }
}

export function getMedicationStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'En cours'
    case 'paused':
      return 'En pause'
    case 'stopped':
      return 'Arrete'
    case 'completed':
      return 'Termine'
    case 'expired':
      return 'Expire'
    default:
      return status
  }
}

export function calculateMedicationDuration(
  startDate: string,
  endDate?: string | null
): { duration: number; unit: string; label: string } {
  const start = new Date(startDate)
  const end = endDate ? new Date(endDate) : new Date()
  const diffTime = Math.abs(end.getTime() - start.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays < 30) {
    return {
      duration: diffDays,
      unit: 'jour',
      label: `${diffDays} jour${diffDays > 1 ? 's' : ''}`
    }
  }

  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30)
    const remainingDays = diffDays % 30

    return {
      duration: months,
      unit: 'mois',
      label:
        remainingDays > 0
          ? `${months} mois ${remainingDays} jour${remainingDays > 1 ? 's' : ''}`
          : `${months} mois`
    }
  }

  const years = Math.floor(diffDays / 365)
  const remainingMonths = Math.floor((diffDays % 365) / 30)

  return {
    duration: years,
    unit: 'annee',
    label:
      remainingMonths > 0
        ? `${years} an${years > 1 ? 's' : ''} ${remainingMonths} mois`
        : `${years} an${years > 1 ? 's' : ''}`
  }
}

export function validateMedicationInput(input: MedicationInput): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  if (!normalizeOptionalText(input.name)) {
    errors.push('Le nom du medicament est obligatoire')
  }

  if (!normalizeOptionalDate(input.start_date)) {
    errors.push('La date de debut est obligatoire')
  }

  if (input.start_date && new Date(input.start_date) > new Date()) {
    errors.push('La date de debut ne peut pas etre dans le futur')
  }

  if (input.planned_end_date && input.start_date) {
    if (new Date(input.planned_end_date) <= new Date(input.start_date)) {
      errors.push('La date de fin prevue doit etre apres la date de debut')
    }
  }

  if (
    typeof input.effectiveness_rating === 'number' &&
    (input.effectiveness_rating < 1 || input.effectiveness_rating > 10)
  ) {
    errors.push("L'evaluation d'efficacite doit etre entre 1 et 10")
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}
